import { createFileRoute, useNavigate } from "@tanstack/react-router";
import useSWR from "swr";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWikidailyStore } from "../lib/games/wikidaily/store";
import {
  selectFoundTargetCount,
  selectTargetCount,
} from "../lib/games/wikidaily/selectors";
import { useGameSocket } from "../lib/useGameSocket";
import { Wikipage } from "../components/Wikipage";
import { api } from "../lib/api";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import { ThemeToggle } from "../components/ThemeToggle";
import { Timer } from "../components/Timer";

import { Timeline } from "../components/Timeline";

import { formatDuration } from "../lib/utils/time";
import type {
  Article,
  FoundArticle,
  ScoreEntry,
  ScoreMove,
} from "../lib/games/wikidaily/types";
import { HomeLink } from "../components/HomeLink";
import { ArticleDialog } from "../components/ArticleDialog";
import { MobileNav } from "../components/navbar/NavbarMobile";
import { NavButtons } from "../components/navbar/NavbarButtons";
import { TargetBadges } from "../components/TargetBadges";
import { Leaderboard } from "../components/Leaderboard";
import { TabButton, TabGroup } from "../components/ui/Tabs";
import { GameLoadingScreen } from "../components/GameLoadingScreen";
import { SectionHeader } from "../components/SectionHeader";
import { StatCard } from "../components/StatCard";
import { EmptyState } from "../components/EmptyState";
import { Button } from "../components/ui/Button";
import { GeometricGridBackground } from "../components/GeometricGridBackground";
import { Podium } from "../components/Podium";
import { ArticleCard } from "../components/ArticleCard";
import { DiscordButton } from "../components/DiscordButton";
import { playFoundSound } from "../lib/sounds";
import { trackEvent } from "../lib/analytics";
import { GeoBingoCard } from "../components/GeoBingoCard";

export const Route = createFileRoute("/daily")({
  component: DailyChallengePage,
});

function formatDate(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
}

function DailyChallengePage() {
  const lang = useAppStore((state) => state.lang);
  const [lobbyId, setLobbyId] = useState<string | null>(null);

  useEffect(() => {
    if (!lobbyId) {
      api.createDailyChallenge(lang).then((id) => {
        setLobbyId(id);
      });
    }
  }, []);

  if (!lobbyId) {
    return (
      <div className="h-dvh flex items-center justify-center bg-black">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 rounded-full border-2 border-zinc-800 border-t-cyan-400 animate-spin" />
          <p className="text-zinc-500 text-sm font-mono">
            Loading daily challenge...
          </p>
        </div>
      </div>
    );
  }

  return <DailyChallengeGame lobbyId={lobbyId} />;
}

function DailyChallengeGame({ lobbyId }: { lobbyId: string }) {
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();
  const lang = useAppStore((state) => state.lang);
  const { send } = useGameSocket(lobbyId, useWikidailyStore);

  // Use selectors to prevent unnecessary re-renders
  const gameState = useWikidailyStore((state) => state.gameState);
  const startGame = useWikidailyStore((state) => state.startGame);
  const giveUp = useWikidailyStore((state) => state.giveUp);

  // Mobile navigation
  const navbarButtonsIngame = [NavButtons.wiki, NavButtons.targets] as const;
  const [mobileTabIngame, setMobileTabIngame] = useState<
    (typeof navbarButtonsIngame)[number]
  >(NavButtons.wiki);

  // Computed values via selectors
  const foundTargetCount = useWikidailyStore(selectFoundTargetCount);

  //TODO: this required becaues daily struct does not have found_targets unlike wikiparty, maybe it should?
  const targetCount = useWikidailyStore(selectTargetCount);
  const prevFoundCount = useRef(targetCount);

  useEffect(() => {
    if (foundTargetCount > prevFoundCount.current) {
      playFoundSound();
    }
    prevFoundCount.current = foundTargetCount;
  }, [foundTargetCount]);

  const [selectedArticle, setSelectedArticle] = useState<{
    title: string;
    description: string | null;
  } | null>(null);

  if (!gameState) {
    return <GameLoadingScreen />;
  }

  if (gameState.phase === "Start") {
    return (
      <div
        className={`h-dvh ${isDark ? "bg-black" : "bg-white"} relative overflow-auto font-sans`}
      >
        <GeometricGridBackground isDark={isDark} />

        {/* Accent Gradient Orbs */}
        {isDark && (
          <>
            <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-radial from-cyan-500/20 via-transparent to-transparent rounded-full blur-3xl opacity-60" />
            <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-radial from-purple-500/15 via-transparent to-transparent rounded-full blur-3xl opacity-40" />
          </>
        )}

        {/* Top Navigation Bar */}
        <div
          className={`fixed top-0 left-0 right-0 z-50 border-b ${isDark ? "border-white/10 bg-black/50" : "border-black/10 bg-white/50"} backdrop-blur-md`}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <HomeLink />
            <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
          </div>
        </div>

        <div className="relative pt-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-16">
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-6 ${
                  isDark
                    ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
                    : "border-blue-500/30 bg-blue-500/10 text-blue-600"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${isDark ? "bg-cyan-400" : "bg-blue-500"} animate-pulse`}
                />
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t("daily.wikipedia", {
                    language: gameState.challenge.language.toUpperCase(),
                  })}
                </span>
              </div>

              <h1
                className={`text-5xl sm:text-6xl font-bold tracking-tight mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {t("daily.title")}
              </h1>

              <p
                className={`text-lg ${isDark ? "text-zinc-500" : "text-gray-400"} font-mono`}
              >
                {formatDate(gameState.challenge.date, lang)}
              </p>
            </div>

            {/* Challenge Cards */}
            <div className="space-y-8 mb-12">
              {/* Start Article */}
              <div className="space-y-3">
                <SectionHeader
                  icon={
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  }
                  title={t("daily.startFrom")}
                  theme={theme}
                  isDark={isDark}
                />
                <ArticleCard
                  article={gameState.challenge.start_article}
                  theme={theme}
                  variant="default"
                  onInfoClick={() =>
                    setSelectedArticle({
                      title: gameState.challenge.start_article.title,
                      description:
                        gameState.challenge.start_article.description,
                    })
                  }
                />
              </div>

              {/* Target Articles */}
              <div className="space-y-3">
                <SectionHeader
                  icon={
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                  title={t("daily.findTargets")}
                  theme={theme}
                  isDark={isDark}
                />
                <div className="space-y-3">
                  {gameState.challenge.target_articles.map(
                    (article: Article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        theme={theme}
                        variant="default"
                        onInfoClick={() =>
                          setSelectedArticle({
                            title: article.title,
                            description: article.description,
                          })
                        }
                      />
                    ),
                  )}
                </div>
              </div>
            </div>

            {/* Start Button */}
            <Button
              variant="primary"
              onClick={() => {
                trackEvent("daily-challenge-start");
                const action = startGame();
                send(action);
              }}
              isDark={isDark}
              data-testid="start-challenge-btn"
            >
              {t("daily.startChallenge")}
            </Button>

            {/* Footer */}
            <div className="text-center mt-16">
              <div
                className={`text-xs ${isDark ? "text-zinc-600" : "text-gray-400"} font-mono`}
              >
                <span className="opacity-50 hidden sm:block">
                  {t("daily.navigateHint")}
                </span>
              </div>
            </div>
          </div>
        </div>
        <ArticleDialog
          isOpen={!!selectedArticle}
          onClose={() => setSelectedArticle(null)}
          title={selectedArticle?.title ?? ""}
          description={selectedArticle?.description ?? null}
          isDark={isDark}
        />
      </div>
    );
  }

  if (gameState.phase === "Game") {
    return (
      <div
        className={`h-dvh ${isDark ? "bg-black" : "bg-white"} flex flex-col font-sans overflow-hidden`}
      >
        {/* Top Navigation Bar */}
        <div
          className={`shrink-0 z-50 border-b ${isDark ? "border-white/[0.08] bg-black/60" : "border-black/[0.06] bg-white/70"} backdrop-blur-xl`}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
            {/* Flex Layout with space-between */}
            <div className="flex items-center justify-between gap-4">
              {/* Left Section */}
              <div className="flex items-center">
                <HomeLink />
              </div>

              {/* Center Section - Takes all available space */}
              <div className="flex-1 flex items-center justify-center gap-3 px-4 min-w-0">
                {/* Desktop: Target Articles */}
                <div className="hidden lg:flex flex-wrap items-center justify-center gap-1.5">
                  {(() => {
                    const foundTargetsMap = new Map<
                      number,
                      { found_time?: [number, number] }
                    >();
                    gameState.found_articles.forEach((fa: FoundArticle) => {
                      if (fa.is_target) {
                        foundTargetsMap.set(fa.article.id, {
                          found_time: fa.found_time,
                        });
                      }
                    });
                    return (
                      <TargetBadges
                        targets={gameState.challenge.target_articles}
                        isFound={(id: number) => !!foundTargetsMap.get(id)}
                        theme={theme}
                        isDark={isDark}
                      />
                    );
                  })()}
                </div>
              </div>

              {/* Right Section */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Timer - All screen sizes */}
                <div
                  className={`flex items-center gap-1.5 font-mono ${isDark ? "text-zinc-400" : "text-gray-600"} text-sm sm:text-xs`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  {gameState.start_time && (
                    <Timer startTime={gameState.start_time} isDark={isDark} />
                  )}
                </div>

                <div
                  className={`hidden sm:block w-px h-4 ${isDark ? "bg-zinc-800" : "bg-gray-200"}`}
                />

                <ThemeToggle isDark={isDark} onToggle={toggleTheme} />

                {/* Give Up Button */}
                <button
                  type="button"
                  data-testid="give-up-btn"
                  onClick={() => {
                    trackEvent("daily-challenge-give-up", {
                      foundTargets: foundTargetCount.toString(),
                    });
                    const action = giveUp();
                    send(action);
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 ${
                    isDark
                      ? "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
                      : "bg-gray-100 text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-900"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${foundTargetCount > 0 ? "bg-emerald-500" : "bg-zinc-400"}`}
                  />
                  <span>
                    {foundTargetCount > 0
                      ? t("daily.submit", {
                          found: foundTargetCount,
                          total: targetCount,
                        })
                      : t("daily.giveUp")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <MobileNav
          currentTab={mobileTabIngame}
          onTabChange={setMobileTabIngame}
          isDark={isDark}
          theme={theme}
          buttons={navbarButtonsIngame}
          className="flex-1"
        >
          <div className="flex overflow-hidden min-h-0 h-full">
            {/* Targets panel - Mobile only */}
            <section
              className={`px-4 py-4 space-y-2 lg:hidden overflow-auto ${mobileTabIngame === NavButtons.targets ? "flex-1" : "hidden"}`}
            >
              <h3
                className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"} mb-4`}
              >
                {t("lobby.targetArticles.title")}
              </h3>
              <div className="flex flex-col justify-start gap-2">
                {(() => {
                  const foundTargetsMap = new Map<
                    number,
                    { found_time: [number, number] }
                  >();
                  gameState.found_articles.forEach((fa: FoundArticle) => {
                    if (fa.is_target) {
                      foundTargetsMap.set(fa.article.id, {
                        found_time: fa.found_time,
                      });
                    }
                  });
                  return (
                    <TargetBadges
                      targets={gameState.challenge.target_articles}
                      isFound={(id: number) => !!foundTargetsMap.get(id)}
                      theme={theme}
                      isDark={isDark}
                      showSeparators={false}
                      renderBadgeContent={(target, isFound) => {
                        const foundInfo = foundTargetsMap.get(target.id);
                        return isFound && foundInfo?.found_time
                          ? formatDuration(foundInfo.found_time[0])
                          : null;
                      }}
                    />
                  );
                })()}
              </div>
            </section>

            {/* Wiki page */}
            <section
              className={`flex-1 overflow-hidden ${mobileTabIngame === NavButtons.wiki ? "flex" : "hidden lg:flex"}`}
            >
              <div className="h-full w-full">
                <Wikipage className="mx-auto" isDark={isDark} send={send} />
              </div>
            </section>
          </div>
        </MobileNav>
      </div>
    );
  }

  if (gameState.phase === "Scoreboard") {
    const timeInSeconds =
      gameState.end_time && gameState.start_time
        ? Math.round(
            (new Date(gameState.end_time).getTime() -
              new Date(gameState.start_time).getTime()) /
              1000,
          )
        : 0;

    return (
      <ScoreboardScreen
        gameState={gameState}
        isDark={isDark}
        toggleTheme={toggleTheme}
        timeInSeconds={timeInSeconds}
      />
    );
  }

  return (
    <div
      className={`min-h-screen ${theme.bg} flex items-center justify-center`}
    >
      <p className={theme.textMuted}>{t("daily.unknownPhase")}</p>
    </div>
  );
}

interface ScoreboardScreenProps {
  gameState: import("../lib/games/wikidaily/types").WikidailyState;
  isDark: boolean;
  toggleTheme: () => void;
  timeInSeconds: number;
}

function ScoreboardScreen({
  gameState,
  isDark,
  toggleTheme,
  timeInSeconds,
}: ScoreboardScreenProps) {
  const navigate = useNavigate();
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<
    "summary" | "timeline" | "scoreboard"
  >("summary");
  const [shareStatus, setShareStatus] = useState<"idle" | "copied" | "error">(
    "idle",
  );

  const scoreboardKey = gameState.challenge.language
    ? `/api/daily/scoreboard?language=${gameState.challenge.language}`
    : null;

  const { data: scoreboard, isLoading: isLoadingScoreboard } = useSWR(
    scoreboardKey,
    (url: string) =>
      fetch(url, { credentials: "include" }).then((r) => r.json()),
    {
      revalidateOnFocus: false,
    },
  );

  const foundTargets = gameState.found_articles.filter(
    (fa: FoundArticle) => fa.is_target,
  );

  const formatShareText = () => {
    const targetArticles = gameState.challenge.target_articles
      .map((a: Article) => a.title)
      .join(", ");

    const path = gameState.found_articles
      .map((fa: FoundArticle) =>
        fa.is_target ? `${fa.article.title} 🎯` : fa.article.title,
      )
      .join(" → ");
    console.log(gameState);

    if (gameState.completed_successfully) {
      const minutes = Math.floor(timeInSeconds / 60);
      const seconds = timeInSeconds % 60;
      const timeStr = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      return t("daily.shareSuccess", {
        date: gameState.challenge.date,
        time: timeStr,
        targets: targetArticles,
        path: path,
      });
    } else {
      const lastFoundTarget =
        gameState.found_articles[gameState.found_articles.length - 1];
      const lastTimestamp = lastFoundTarget?.found_time ?? [0, 0];
      const minutes = Math.floor(lastTimestamp[0] / 60);
      const seconds = lastTimestamp[0] % 60;
      const timeStr = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      return t("daily.shareGiveUp", {
        date: gameState.challenge.date,
        time: timeStr,
        targets: targetArticles,
        path: path,
      });
    }
  };

  const handleShare = async () => {
    trackEvent("daily-challenge-share-results", {
      completed: String(gameState.completed_successfully ?? false),
      foundTargets: foundTargets.length.toString(),
    });
    const text = formatShareText();
    try {
      await navigator.clipboard.writeText(text);
      setShareStatus("copied");
      setTimeout(() => setShareStatus("idle"), 2000);
    } catch {
      setShareStatus("error");
      setTimeout(() => setShareStatus("idle"), 2000);
    }
  };

  const timelinePlayers =
    scoreboard?.scores.map((score: ScoreEntry) => ({
      name: score.player_name,
      articles: score.moves.map((move: ScoreMove) => ({
        text: move.title,
        duration: move.duration_seconds,
        type: move.is_target ? ("highlight" as const) : undefined,
      })),
    })) || [];

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-black" : "bg-white"} relative overflow-hidden font-sans`}
    >
      <GeometricGridBackground isDark={isDark} />

      {/* Accent Gradient Orbs */}
      {isDark && (
        <>
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-radial from-cyan-500/20 via-transparent to-transparent rounded-full blur-3xl opacity-60" />
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-radial from-purple-500/15 via-transparent to-transparent rounded-full blur-3xl opacity-40" />
        </>
      )}

      {/* Top Navigation Bar */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 border-b ${isDark ? "border-white/10 bg-black/50" : "border-black/10 bg-white/50"} backdrop-blur-md`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <HomeLink />
            <div
              className={`hidden sm:block w-px h-4 ${isDark ? "bg-white/10" : "bg-black/10"}`}
            />
            <span
              className={`text-sm ${isDark ? "text-zinc-500" : "text-gray-400"} font-mono hidden sm:block`}
            >
              {gameState.challenge.date}
            </span>
          </div>
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
        </div>
      </div>

      <div className="relative pt-20 pb-20">
        {/* Hero Section */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex flex-col items-center text-center">
            {/* Main Title */}
            <h1
              className={`text-5xl sm:text-6xl font-bold tracking-tight mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {gameState.completed_successfully
                ? t("daily.complete")
                : t("daily.ended")}
            </h1>

            {/* Subtitle with monospace accent */}
            <p
              className={`text-lg ${isDark ? "text-zinc-400" : "text-gray-500"} max-w-md`}
            >
              {gameState.completed_successfully
                ? t("daily.foundAll")
                : t("daily.foundOf", {
                    found: foundTargets.length,
                    total: gameState.challenge.target_articles.length,
                  })}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gradient-to-r from-transparent via-current to-transparent opacity-70">
            <StatCard
              label={t("daily.stats.time")}
              value={
                gameState.completed_successfully
                  ? formatDuration(timeInSeconds)
                  : "—"
              }
              isDark={isDark}
              delay={0}
            />
            <StatCard
              label={t("daily.stats.targets")}
              value={`${foundTargets.length}/${gameState.challenge.target_articles.length}`}
              isDark={isDark}
              delay={1}
            />
            <StatCard
              label={t("daily.stats.clicks")}
              value={gameState.found_articles.length.toString()}
              isDark={isDark}
              delay={2}
            />
            <StatCard
              label={t("daily.stats.started")}
              value={gameState.challenge.start_article.title}
              isDark={isDark}
              delay={3}
              truncate
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <TabGroup theme={theme} className="flex">
            <TabButton
              active={activeTab === "summary"}
              onClick={() => {
                trackEvent("daily-challenge-summary-tab");
                setActiveTab("summary");
              }}
              isDark={isDark}
              theme={theme}
              className="flex-1"
              data-testid="tab-summary"
            >
              {t("daily.tabs.summary")}
            </TabButton>
            <TabButton
              active={activeTab === "timeline"}
              onClick={() => {
                trackEvent("daily-challenge-timeline-tab");
                setActiveTab("timeline");
              }}
              isDark={isDark}
              theme={theme}
              className="flex-1"
              data-testid="tab-paths"
            >
              {t("daily.tabs.paths")}
            </TabButton>
            <TabButton
              active={activeTab === "scoreboard"}
              onClick={() => {
                trackEvent("daily-challenge-leaderboard-tab");
                setActiveTab("scoreboard");
              }}
              isDark={isDark}
              theme={theme}
              className="flex-1"
              data-testid="tab-leaderboard"
            >
              {t("daily.tabs.leaderboard")}
            </TabButton>
          </TabGroup>
        </div>

        {/* Content Area */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === "summary" && (
            <div className="space-y-8 animate-in fade-in duration-500">
              {/* Targets Section */}
              <div
                className={`rounded-2xl border ${isDark ? "border-zinc-800 bg-zinc-950/50" : "border-gray-200 bg-gray-50/50"} p-6`}
              >
                <SectionHeader
                  icon={
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                  title={t("daily.targetsFound")}
                  theme={theme}
                  isDark={isDark}
                />

                <div className="space-y-3">
                  {gameState.challenge.target_articles.map(
                    (target: Article, index: number) => {
                      const found = gameState.found_articles.find(
                        (fa: FoundArticle) =>
                          fa.article.id === target.id && fa.is_target,
                      );
                      return (
                        <TargetRow
                          key={target.id}
                          target={target}
                          found={found}
                          index={index}
                          isDark={isDark}
                        />
                      );
                    },
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 ">
                <Button
                  onClick={handleShare}
                  variant="secondary"
                  isDark={isDark}
                  className="flex-1"
                  data-testid="share-results-btn"
                >
                  <span className="flex items-center justify-center gap-2">
                    {shareStatus === "copied" ? (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {t("daily.copied")}
                      </>
                    ) : shareStatus === "error" ? (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        {t("daily.failed")}
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                          />
                        </svg>
                        {t("daily.shareResults")}
                      </>
                    )}
                  </span>
                </Button>

                <Button
                  variant="primary"
                  onClick={() => {
                    trackEvent("daily-challenge-play-another-game");
                    api
                      .createLobby(gameState.challenge.language)
                      .then((id) => navigate({ to: `/wikiparty/${id}` }));
                  }}
                  isDark={isDark}
                  data-testid="play-more-btn"
                >
                  {t("daily.playMore")}
                </Button>
                <DiscordButton />
              </div>

              <GeoBingoCard isDark={isDark} variant="daily" />
            </div>
          )}

          {activeTab === "timeline" && (
            <div
              className={`rounded-2xl border ${isDark ? "border-zinc-800 bg-zinc-950/50" : "border-gray-200 bg-gray-50/50"} p-6 animate-in fade-in duration-500`}
            >
              <SectionHeader
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.447-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                }
                title={t("daily.playerPaths")}
                theme={theme}
                isDark={isDark}
              />

              {timelinePlayers.length > 0 ? (
                <Timeline players={timelinePlayers} isDark={isDark} />
              ) : (
                <EmptyState
                  isDark={isDark}
                  message={t("daily.noPathData")}
                  icon={
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.447-.894L15 4m0 13V4m0 0L9 7"
                      />
                    </svg>
                  }
                />
              )}
            </div>
          )}

          {activeTab === "scoreboard" && (
            <div
              className={`rounded-2xl border ${isDark ? "border-zinc-800 bg-zinc-950/50" : "border-gray-200 bg-gray-50/50"} p-6 animate-in fade-in duration-500`}
            >
              <SectionHeader
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                }
                title={t("daily.todayLeaderboard")}
                theme={theme}
                isDark={isDark}
              />

              {/* Podium for Top 3 */}
              {!isLoadingScoreboard &&
                scoreboard &&
                scoreboard.scores.length > 0 && (
                  <Podium
                    players={scoreboard.scores
                      .slice(0, 3)
                      .map((score: ScoreEntry) => ({
                        id: score.player_name,
                        name: score.player_name,
                        targetsFound: score.found_count,
                      }))}
                    isDark={isDark}
                    currentPlayerId="You"
                    className="mb-8"
                  />
                )}

              {isLoadingScoreboard ? (
                <div className="flex justify-center py-16">
                  <div
                    className={`w-8 h-8 rounded-full border-2 ${isDark ? "border-zinc-800 border-t-cyan-400" : "border-gray-200 border-t-blue-500"} animate-spin`}
                  />
                </div>
              ) : scoreboard && scoreboard.scores.length > 0 ? (
                <Leaderboard
                  entries={scoreboard.scores.map((score: ScoreEntry) => ({
                    name: score.player_name,
                    time: score.completion_time_seconds,
                    clicks: score.moves.length,
                    meta: `${score.found_count}/${score.target_count}`,
                    isCurrentUser: score.player_name === "You",
                  }))}
                  isDark={isDark}
                  theme={theme}
                  rounded={true}
                  showDividers={false}
                  maxItems={20}
                />
              ) : (
                <EmptyState
                  isDark={isDark}
                  message={t("daily.noScores")}
                  icon={
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  }
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 text-center">
          <div
            className={`text-xs ${isDark ? "text-zinc-600" : "text-gray-400"} font-mono`}
          >
            <span className="opacity-50">WikiParty</span>
            <span className="mx-2 opacity-30">/</span>
            <span className="opacity-50">Daily Challenge</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TargetRow({
  target,
  found,
  index,
  isDark,
}: {
  target: Article;
  found: FoundArticle | undefined;
  index: number;
  isDark: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 ${
        found
          ? isDark
            ? "bg-zinc-900/50 border-zinc-800"
            : "bg-white border-gray-200"
          : isDark
            ? "bg-zinc-950/30 border-zinc-900/50 opacity-50"
            : "bg-gray-100/50 border-gray-200/50 opacity-50"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-mono ${
          found
            ? isDark
              ? "bg-cyan-500/10 text-cyan-400"
              : "bg-blue-500/10 text-blue-600"
            : isDark
              ? "bg-zinc-900 text-zinc-600"
              : "bg-gray-200 text-gray-400"
        }`}
      >
        {String(index + 1).padStart(2, "0")}
      </div>

      <div className="flex-1 min-w-0">
        <div
          className={`font-medium truncate ${found ? (isDark ? "text-white" : "text-gray-900") : isDark ? "text-zinc-600" : "text-gray-400"}`}
        >
          {target.title}
        </div>
        {found && found.found_time && (
          <div
            className={`text-xs mt-0.5 font-mono ${isDark ? "text-zinc-500" : "text-gray-400"}`}
          >
            {t("daily.foundIn", {
              duration: formatDuration(found.found_time[0]),
            })}
          </div>
        )}
      </div>

      {found ? (
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isDark
              ? "bg-cyan-500/20 text-cyan-400"
              : "bg-blue-500/20 text-blue-600"
          }`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      ) : (
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isDark ? "bg-zinc-900 text-zinc-600" : "bg-gray-200 text-gray-400"
          }`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
